// NoticeAudit.js
// Reconciliation workspace for assessment notices that have been processed
// into parcel_values rows. Each card represents one (parcel, year) record.
// Staff verify the 9 fields that flow into the Sigertax CSV export, optionally
// open the source notice PDF, then mark the row audited.
//
// Export contract (the columns Sigertax imports):
//   Sys Year             <- parcel_values.tax_year
//   Client Name          <- parcels.client_name
//   Property Name        <- properties.name
//   Assessor             <- parcel_values.assessor
//   Owner Name           <- parcel_values.owner_name
//   Account #/Asssr ID   <- parcel_values.account_number
//   CurrentLandFMV       <- parcel_values.land_value
//   CurrentImpFMV        <- parcel_values.improvement_value
//   MarketValue          <- parcel_values.market_value
//   + sigertax_prop_id, sigertax_parcel_id (matching keys)

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../supabase';
import { fileUrl } from '../utils/fileUrl';
import { DOC_KEYS } from '../utils/docVocab';
import { ORIGINAL_NOTICE } from '../utils/notices';
import { PARCEL_STATUS } from '../constants/statusCodes';

const STATE_OPTIONS = ['TX', 'GA', 'TN', 'FL', 'NC', 'SC', 'AL', 'LA', 'MS', 'AR', 'OK'];

export default function NoticeAudit({ darkMode, staffUser, allClients = [] }) {
  const isDark = !!darkMode;

  const c = {
    bg:        isDark ? '#0f1729' : 'var(--bg)',
    card:      isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
    cardBorder:isDark ? 'rgba(255,255,255,0.1)'  : 'var(--line)',
    text:      isDark ? '#e8eaf2' : 'var(--ink)',
    muted:     isDark ? '#8892aa' : 'var(--slate)',
    sub:       isDark ? '#a8b0cc' : '#4a5068',
    gold:      'var(--red)',
    inputBg:   isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
    inputBorder:isDark ? 'rgba(255,255,255,0.15)' : 'var(--line)',
    headerBg:  isDark ? 'rgba(26,34,56,0.6)' : '#fafbfd',
    rowBg:     isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
  };

  const s = {
    label: { fontSize:'10px', fontWeight:'700', color:c.muted, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'4px' },
    input: { width:'100%', padding:'7px 9px', borderRadius:'6px', border:`1px solid ${c.inputBorder}`, background:c.inputBg, color:c.text, fontSize:'12px', fontFamily:"'Public Sans',system-ui,sans-serif" },
    inputMono: { width:'100%', padding:'7px 9px', borderRadius:'6px', border:`1px solid ${c.inputBorder}`, background:c.inputBg, color:c.text, fontSize:'12px', fontFamily:"'DM Mono',monospace" },
    select: { width:'100%', padding:'7px 9px', borderRadius:'6px', border:`1px solid ${c.inputBorder}`, background:c.inputBg, color:c.text, fontSize:'12px' },
    badge: (rgb) => ({ display:'inline-flex', alignItems:'center', padding:'2px 8px', borderRadius:'10px', background:`rgba(${rgb},0.15)`, color:`rgb(${rgb})`, fontSize:'11px', fontWeight:'700', whiteSpace:'nowrap' }),
    btn: { padding:'7px 14px', borderRadius:'7px', border:'none', cursor:'pointer', fontSize:'12px', fontWeight:'600', fontFamily:"'Public Sans',system-ui,sans-serif" },
  };

  // ── State ───────────────────────────────────────────────────
  const [taxYear, setTaxYear] = useState('2026');
  const [stateFilter, setStateFilter] = useState('TX');
  const [clientFilter, setClientFilter] = useState('');
  const [assessorFilter, setAssessorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('unaudited'); // unaudited | audited | all
  const [hasNoticeFilter, setHasNoticeFilter] = useState('all'); // all | yes | no
  const [search, setSearch] = useState('');

  const [rows, setRows] = useState([]);  // joined: parcel_values + parcels + properties + notice
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState('');

  // Per-row local edits (uncommitted) — keyed by parcel_values.id
  const [edits, setEdits] = useState({});

  // ── Data loader ─────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // 1. Parcel values for the year + state (joined to parcels for state filter)
      // Supabase doesn't filter on joined columns easily; fetch parcel_values + parcels
      // and filter client-side. Volume: ~230 rows for TX 2026 — fine.
      const { data: pvRaw, error: pvErr } = await supabase
        .from('parcel_values')
        .select('id, parcel_id, tax_year, owner_name, account_number, assessor, land_value, improvement_value, market_value, is_primary, audited_at, audited_by')
        .eq('tax_year', taxYear)
        .eq('notice_no', ORIGINAL_NOTICE)
        .eq('is_primary', true);
      if (pvErr) throw pvErr;

      const parcelIds = [...new Set((pvRaw || []).map(r => r.parcel_id).filter(Boolean))];
      if (parcelIds.length === 0) {
        setRows([]);
        setLoading(false);
        return;
      }

      // 2. Parcels (for state filter, client name, sigertax IDs, parcel name)
      const { data: parcelsRaw, error: pErr } = await supabase
        .from('parcels')
        .select('id, parcel_name, client_name, state, property_id, master_property_id, sigertax_prop_id, sigertax_parcel_id, account_number, parcel_status')
        .in('id', parcelIds);
      if (pErr) throw pErr;

      const parcelById = new Map((parcelsRaw || []).map(p => [p.id, p]));

      // 3. Properties (for property name)
      const propertyIds = [...new Set((parcelsRaw || []).map(p => p.property_id).filter(Boolean))];
      const propertyById = new Map();
      if (propertyIds.length) {
        const { data: propsRaw } = await supabase
          .from('properties')
          .select('id, name, master_property_id')
          .in('id', propertyIds);
        (propsRaw || []).forEach(p => propertyById.set(p.id, p));
      }

      // 4. Notices: most recent Notice file per parcel for this year
      const { data: notesRaw } = await supabase
        .from('parcel_documents')
        .select('parcel_id, file_exchange_id, year_subfolder, folder_name, created_at')
        .in('parcel_id', parcelIds)
        .eq('folder_key', DOC_KEYS.notices)
        .eq('year_subfolder', taxYear)
        .order('created_at', { ascending: false });

      const noticeByParcel = new Map();
      (notesRaw || []).forEach(n => {
        if (!noticeByParcel.has(n.parcel_id)) noticeByParcel.set(n.parcel_id, n);
      });

      // 5. Join file_exchange for the notice files we found
      const fxIds = [...new Set([...noticeByParcel.values()].map(n => n.file_exchange_id).filter(Boolean))];
      const fxById = new Map();
      if (fxIds.length) {
        const { data: fxRaw } = await supabase
          .from('file_exchange')
          .select('id, file_name, storage_path, file_size_bytes')
          .in('id', fxIds);
        (fxRaw || []).forEach(fx => fxById.set(fx.id, fx));
      }

      // 6. Join everything client-side and apply state filter
      const joined = (pvRaw || [])
        .map(pv => {
          const parcel = parcelById.get(pv.parcel_id);
          if (!parcel) return null;
          const property = parcel.property_id ? propertyById.get(parcel.property_id) : null;
          const noticeLink = noticeByParcel.get(pv.parcel_id);
          const notice = noticeLink ? fxById.get(noticeLink.file_exchange_id) : null;
          return {
            pv_id: pv.id,
            parcel_id: pv.parcel_id,
            tax_year: pv.tax_year,
            owner_name: pv.owner_name || '',
            account_number: pv.account_number || '',
            assessor: pv.assessor || '',
            land_value: pv.land_value,
            improvement_value: pv.improvement_value,
            market_value: pv.market_value,
            audited_at: pv.audited_at,
            audited_by: pv.audited_by,
            parcel_name: parcel.parcel_name || '',
            client_name: parcel.client_name || '',
            state: parcel.state || '',
            sigertax_prop_id: parcel.sigertax_prop_id || null,
            sigertax_parcel_id: parcel.sigertax_parcel_id || null,
            parcel_account_number: parcel.account_number || '',
            parcel_status: parcel.parcel_status || PARCEL_STATUS.active,
            property_name: property?.name || '',
            notice_file_id: notice?.id || null,
            notice_file_name: notice?.file_name || null,
            notice_storage_path: notice?.storage_path || null,
          };
        })
        .filter(r => r && (!stateFilter || r.state === stateFilter || (stateFilter === 'TX' && !r.state)));
        // ^ TX fallback: parcels with no state set are assumed TX (legacy data)

      setRows(joined);
    } catch (e) {
      console.error('NoticeAudit load error:', e);
      setError(e.message || 'Failed to load audit queue');
    } finally {
      setLoading(false);
    }
  }, [taxYear, stateFilter]);

  useEffect(() => { load(); }, [load]);

  // ── Filtered view ───────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return rows.filter(r => {
      if (clientFilter && r.client_name !== clientFilter) return false;
      if (assessorFilter && r.assessor !== assessorFilter) return false;
      if (statusFilter === 'unaudited' && r.audited_at) return false;
      if (statusFilter === 'audited' && !r.audited_at) return false;
      if (hasNoticeFilter === 'yes' && !r.notice_file_id) return false;
      if (hasNoticeFilter === 'no' && r.notice_file_id) return false;
      if (q) {
        const hay = `${r.client_name} ${r.property_name} ${r.parcel_name} ${r.owner_name} ${r.account_number} ${r.assessor} ${r.sigertax_prop_id || ''} ${r.sigertax_parcel_id || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, clientFilter, assessorFilter, statusFilter, hasNoticeFilter, search]);

  const counts = useMemo(() => {
    const total = rows.length;
    const audited = rows.filter(r => r.audited_at).length;
    return { total, audited, unaudited: total - audited };
  }, [rows]);

  const distinctClients  = useMemo(() => [...new Set(rows.map(r => r.client_name).filter(Boolean))].sort(), [rows]);
  const distinctAssessors = useMemo(() => [...new Set(rows.map(r => r.assessor).filter(Boolean))].sort(), [rows]);

  // ── Actions ─────────────────────────────────────────────────
  function getEditedRow(row) {
    return { ...row, ...(edits[row.pv_id] || {}) };
  }

  function setField(pvId, field, value) {
    setEdits(prev => ({ ...prev, [pvId]: { ...(prev[pvId] || {}), [field]: value } }));
  }

  function isDirty(pvId) {
    return edits[pvId] && Object.keys(edits[pvId]).length > 0;
  }

  async function saveRow(row, opts = {}) {
    const merged = getEditedRow(row);
    setSavingId(row.pv_id);
    try {
      // Update parcel_values
      const pvUpdate = {
        owner_name: merged.owner_name || null,
        account_number: merged.account_number || null,
        assessor: merged.assessor || null,
        land_value: merged.land_value === '' || merged.land_value == null ? null : Number(merged.land_value),
        improvement_value: merged.improvement_value === '' || merged.improvement_value == null ? null : Number(merged.improvement_value),
        market_value: merged.market_value === '' || merged.market_value == null ? null : Number(merged.market_value),
      };
      if (opts.markAudited) {
        pvUpdate.audited_at = new Date().toISOString();
        pvUpdate.audited_by = staffUser?.name || 'NTRG Staff';
      }
      const { error: pvErr } = await supabase.from('parcel_values').update(pvUpdate).eq('id', row.pv_id);
      if (pvErr) throw pvErr;

      // Update parcels.sigertax_*  if changed
      const parcelUpdate = {};
      if (merged.sigertax_prop_id !== row.sigertax_prop_id) {
        parcelUpdate.sigertax_prop_id = merged.sigertax_prop_id ? parseInt(merged.sigertax_prop_id, 10) : null;
      }
      if (merged.sigertax_parcel_id !== row.sigertax_parcel_id) {
        parcelUpdate.sigertax_parcel_id = merged.sigertax_parcel_id ? parseInt(merged.sigertax_parcel_id, 10) : null;
      }
      if (Object.keys(parcelUpdate).length) {
        const { error: pErr } = await supabase.from('parcels').update(parcelUpdate).eq('id', row.parcel_id);
        if (pErr) throw pErr;
      }

      // Clear local edits, refresh row in place
      setEdits(prev => { const next = { ...prev }; delete next[row.pv_id]; return next; });
      setRows(prev => prev.map(r => r.pv_id === row.pv_id ? {
        ...r,
        ...pvUpdate,
        sigertax_prop_id: parcelUpdate.sigertax_prop_id !== undefined ? parcelUpdate.sigertax_prop_id : r.sigertax_prop_id,
        sigertax_parcel_id: parcelUpdate.sigertax_parcel_id !== undefined ? parcelUpdate.sigertax_parcel_id : r.sigertax_parcel_id,
      } : r));
    } catch (e) {
      console.error('saveRow error:', e);
      alert('Save failed: ' + (e.message || e));
    } finally {
      setSavingId(null);
    }
  }

  async function unverify(row) {
    setSavingId(row.pv_id);
    try {
      const { error: e1 } = await supabase.from('parcel_values').update({ audited_at: null, audited_by: null }).eq('id', row.pv_id);
      if (e1) throw e1;
      setRows(prev => prev.map(r => r.pv_id === row.pv_id ? { ...r, audited_at: null, audited_by: null } : r));
    } catch (e) {
      alert('Unverify failed: ' + (e.message || e));
    } finally {
      setSavingId(null);
    }
  }

  async function openNotice(row) {
    if (!row.notice_storage_path) return;
    try {
      const url = await fileUrl(row.notice_storage_path, 'open');
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      alert('Could not open notice: ' + (e.message || e));
    }
  }

  // ── CSV export (audited rows only, in Sigertax import order) ──
  function downloadCsv() {
    const auditedRows = rows.filter(r => r.audited_at);
    if (auditedRows.length === 0) { alert('No audited rows to export.'); return; }

    const headers = [
      'Sys Year','Client Name','Property Name','Assessor','Owner Name',
      'Account #/Assessor ID','CurrentLandFMV','CurrentImpFMV','MarketValue',
      'Sigertax PropID','Sigertax ParcelID',
    ];
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s2 = String(v);
      return /[",\n]/.test(s2) ? `"${s2.replace(/"/g,'""')}"` : s2;
    };
    // excelText wraps a value as Excel literal-text so long numeric strings
    // (e.g. 1207670010002) don't get mangled into scientific notation on open.
    // Copy-paste from the rendered cell still yields the clean value.
    const excelText = (v) => {
      if (v === null || v === undefined || v === '') return '';
      const s2 = String(v).replace(/"/g, '""');
      return `"=""${s2}"""`;
    };
    const lines = [headers.join(',')];
    auditedRows
      .sort((a,b) => (a.client_name||'').localeCompare(b.client_name||'') || (a.property_name||'').localeCompare(b.property_name||'') || (a.assessor||'').localeCompare(b.assessor||''))
      .forEach(r => {
        lines.push([
          escape(r.tax_year),
          escape(r.client_name),
          escape(r.property_name),
          escape(r.assessor),
          escape(r.owner_name),
          excelText(r.account_number),
          escape(r.land_value),
          escape(r.improvement_value),
          escape(r.market_value),
          escape(r.sigertax_prop_id),
          escape(r.sigertax_parcel_id),
        ].join(','));
      });

    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `notice_audit_${stateFilter}_${taxYear}_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ── Render ──────────────────────────────────────────────────
  return (
    <div style={{ width:'100%', maxWidth:'1400px', margin:'0 auto', fontFamily:"'Public Sans',system-ui,sans-serif" }}>
      {/* Header */}
      <div style={{ marginBottom:'18px' }}>
        <div style={{ fontFamily:"'Archivo',sans-serif", fontSize:'24px', fontWeight:'700', color:c.text, marginBottom:'4px' }}>Notice Audit</div>
        <div style={{ fontSize:'13px', color:c.muted }}>
          Review and verify parcel values against assessment notices before exporting to Sigertax.
        </div>
      </div>

      {/* Counter + actions */}
      <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'14px', flexWrap:'wrap' }}>
        <div style={{ ...s.badge('96,165,250'), fontSize:'12px', padding:'4px 12px' }}>
          {counts.audited} of {counts.total} audited
        </div>
        {counts.total > 0 && (
          <div style={{ flex:1, height:'8px', borderRadius:'4px', background:isDark?'rgba(255,255,255,0.06)':'#e8eaf2', overflow:'hidden', maxWidth:'300px' }}>
            <div style={{ width:`${(counts.audited/counts.total)*100}%`, height:'100%', background:'linear-gradient(90deg, #34d399, #60a5fa)' }} />
          </div>
        )}
        <button onClick={downloadCsv} disabled={counts.audited === 0}
          style={{ ...s.btn, background: counts.audited === 0 ? c.muted : 'linear-gradient(180deg,#34d399,#10b981)', color:'#fff', cursor: counts.audited === 0 ? 'not-allowed' : 'pointer' }}>
          ⬇ Download CSV ({counts.audited} audited)
        </button>
      </div>

      {/* Filters */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 100px 100px 200px 150px 120px 120px', gap:'10px', marginBottom:'18px', alignItems:'end' }}>
        <div>
          <div style={s.label}>Search</div>
          <input style={s.input} placeholder="property, parcel, owner, account#, sigertax id..." value={search} onChange={e=>setSearch(e.target.value)} />
        </div>
        <div>
          <div style={s.label}>Year</div>
          <select style={s.select} value={taxYear} onChange={e=>setTaxYear(e.target.value)}>
            {['2024','2025','2026','2027'].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <div style={s.label}>State</div>
          <select style={s.select} value={stateFilter} onChange={e=>setStateFilter(e.target.value)}>
            {STATE_OPTIONS.map(st => <option key={st} value={st}>{st}</option>)}
          </select>
        </div>
        <div>
          <div style={s.label}>Client</div>
          <select style={s.select} value={clientFilter} onChange={e=>setClientFilter(e.target.value)}>
            <option value="">All clients</option>
            {distinctClients.map(cl => <option key={cl} value={cl}>{cl}</option>)}
          </select>
        </div>
        <div>
          <div style={s.label}>Assessor</div>
          <select style={s.select} value={assessorFilter} onChange={e=>setAssessorFilter(e.target.value)}>
            <option value="">All</option>
            {distinctAssessors.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <div style={s.label}>Status</div>
          <select style={s.select} value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}>
            <option value="unaudited">Unaudited</option>
            <option value="audited">Audited</option>
            <option value="all">All</option>
          </select>
        </div>
        <div>
          <div style={s.label}>Notice</div>
          <select style={s.select} value={hasNoticeFilter} onChange={e=>setHasNoticeFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="yes">Has PDF</option>
            <option value="no">No PDF</option>
          </select>
        </div>
      </div>

      {/* Body */}
      {loading && <div style={{ padding:'40px', textAlign:'center', color:c.muted }}>Loading audit queue…</div>}
      {error && <div style={{ padding:'14px', background:'rgba(239,68,68,0.1)', color:'#ef4444', borderRadius:'8px', marginBottom:'12px' }}>{error}</div>}
      {!loading && filtered.length === 0 && (
        <div style={{ padding:'40px', textAlign:'center', color:c.muted, background:c.card, border:`1px solid ${c.cardBorder}`, borderRadius:'10px' }}>
          {rows.length === 0 ? 'No parcel values found for this year + state.' : 'No rows match your filters.'}
        </div>
      )}

      {/* Cards */}
      {!loading && filtered.map(row => {
        const r = getEditedRow(row);
        const dirty = isDirty(row.pv_id);
        const verified = !!row.audited_at;
        const saving = savingId === row.pv_id;
        return (
          <div key={row.pv_id} style={{
            background: c.card, border:`1px solid ${verified ? 'rgba(52,211,153,0.4)' : c.cardBorder}`, borderLeft: verified ? '3px solid #34d399' : `3px solid ${c.cardBorder}`,
            borderRadius:'10px', padding:'14px 18px', marginBottom:'12px',
            opacity: verified && statusFilter === 'all' ? 0.85 : 1,
          }}>
            {/* Row header */}
            <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'12px', flexWrap:'wrap' }}>
              <div style={{ fontWeight:'700', fontSize:'14px', color:c.gold, fontFamily:"'Archivo',sans-serif" }}>
                {row.property_name || '—'}
              </div>
              <div style={{ fontSize:'12px', color:c.sub }}>·</div>
              <div style={{ fontSize:'13px', color:c.text, fontWeight:'600' }}>{row.parcel_name || '—'}</div>
              <div style={{ fontSize:'11px', color:c.muted }}>· {row.client_name}</div>
              <div style={{ flex:1 }} />
              {verified ? (
                <span style={s.badge('52,211,153')}>✓ Verified by {row.audited_by} · {new Date(row.audited_at).toLocaleDateString()}</span>
              ) : (
                <span style={s.badge('251,191,36')}>Unaudited</span>
              )}
              {row.notice_file_id ? (
                <button onClick={() => openNotice(row)} style={{ ...s.btn, background:isDark?'rgba(96,165,250,0.15)':'rgba(96,165,250,0.1)', color:'#60a5fa', padding:'5px 11px' }}>
                  📄 Open Notice
                </button>
              ) : (
                <span style={s.badge('239,68,68')}>No notice on file</span>
              )}
            </div>

            {/* Editable field grid */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:'10px', marginBottom:'10px' }}>
              <div>
                <div style={s.label}>Owner Name</div>
                <input style={s.input} value={r.owner_name} onChange={e => setField(row.pv_id, 'owner_name', e.target.value)} />
              </div>
              <div>
                <div style={s.label}>Account # / Assessor ID</div>
                <input style={s.inputMono} value={r.account_number} onChange={e => setField(row.pv_id, 'account_number', e.target.value)} />
              </div>
              <div>
                <div style={s.label}>Assessor</div>
                <input style={s.input} value={r.assessor} onChange={e => setField(row.pv_id, 'assessor', e.target.value)} />
              </div>
              <div>
                <div style={s.label}>Sys Year</div>
                <input style={{ ...s.input, opacity:0.7 }} value={r.tax_year} disabled />
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr) 1fr 1fr', gap:'10px', marginBottom:'10px' }}>
              <div>
                <div style={s.label}>Current Land FMV</div>
                <input style={s.inputMono} value={r.land_value ?? ''} onChange={e => setField(row.pv_id, 'land_value', e.target.value.replace(/[^0-9.]/g, ''))} />
              </div>
              <div>
                <div style={s.label}>Current Imp FMV</div>
                <input style={s.inputMono} value={r.improvement_value ?? ''} onChange={e => setField(row.pv_id, 'improvement_value', e.target.value.replace(/[^0-9.]/g, ''))} />
              </div>
              <div>
                <div style={s.label}>Market Value</div>
                <input style={s.inputMono} value={r.market_value ?? ''} onChange={e => setField(row.pv_id, 'market_value', e.target.value.replace(/[^0-9.]/g, ''))} />
              </div>
              <div>
                <div style={s.label}>Sigertax PropID</div>
                <input style={s.inputMono} value={r.sigertax_prop_id ?? ''} onChange={e => setField(row.pv_id, 'sigertax_prop_id', e.target.value.replace(/[^0-9]/g, ''))} placeholder="e.g. 77013" />
              </div>
              <div>
                <div style={s.label}>Sigertax ParcelID</div>
                <input style={s.inputMono} value={r.sigertax_parcel_id ?? ''} onChange={e => setField(row.pv_id, 'sigertax_parcel_id', e.target.value.replace(/[^0-9]/g, ''))} placeholder="e.g. 125492" />
              </div>
            </div>

            {/* Action row */}
            <div style={{ display:'flex', alignItems:'center', gap:'8px', justifyContent:'flex-end' }}>
              {dirty && (
                <span style={{ fontSize:'11px', color:'#f59e0b', fontWeight:'600' }}>Unsaved changes</span>
              )}
              <button
                onClick={() => saveRow(row)}
                disabled={!dirty || saving}
                style={{ ...s.btn, background: dirty ? (isDark?'rgba(96,165,250,0.2)':'rgba(96,165,250,0.15)') : 'transparent', color: dirty ? '#60a5fa' : c.muted, cursor: dirty && !saving ? 'pointer' : 'not-allowed' }}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
              {verified ? (
                <button
                  onClick={() => unverify(row)}
                  disabled={saving}
                  style={{ ...s.btn, background:isDark?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.04)', color:c.sub }}>
                  Unverify
                </button>
              ) : (
                <button
                  onClick={() => saveRow(row, { markAudited: true })}
                  disabled={saving}
                  style={{ ...s.btn, background:'linear-gradient(180deg,#34d399,#10b981)', color:'#fff' }}>
                  {saving ? 'Saving…' : (dirty ? '✓ Save & Mark Verified' : '✓ Mark Verified')}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
